// The companies that pay (decision 26 of s10-signup): an account's own list, each with the workspaces it pays for. Only the account that
// owns a company reads or changes it.
import { z } from "zod";
import { IsoDateTime, Uuid } from "./common";
import { Country } from "./identity";
import { PLAN_CODES } from "./pricing";

const Plan = z.enum(PLAN_CODES);
const Status = z.enum(["none", "incomplete", "trialing", "active", "past_due", "unpaid", "canceled"]);

/** The card trial (decision 5 of s10-signup): its days, and the e-mails to customers a workspace may send during it. */
export const TRIAL_DAYS = 14;
export const TRIAL_EMAIL_LIMIT = 500;
/** Why a trial is not given: the account, its e-mail, the company's CUI or the card already had one. */
export const TRIAL_REFUSALS = ["account", "email", "tax_id", "card"] as const;
export const TrialRefusal = z.enum(TRIAL_REFUSALS);
export type TrialRefusal = z.infer<typeof TrialRefusal>;

/** Stripe holds at most this many items on a subscription in `flexible` billing mode: one per workspace. */
export const MAX_WORKSPACES_PER_COMPANY = 100;

export const COMPANY_VERIFIERS = ["anaf", "vies"] as const;
export const CompanyVerifier = z.enum(COMPANY_VERIFIERS);
export type CompanyVerifier = z.infer<typeof CompanyVerifier>;

export const CompanyAddressDto = z.object({
  line1: z.string(),
  line2: z.string().nullable(),
  city: z.string().nullable(),
  postalCode: z.string().nullable(),
  region: z.string().nullable(),
});
export type CompanyAddressDto = z.infer<typeof CompanyAddressDto>;

const Text = (max: number) => z.string().trim().min(1).max(max);
export const CompanyAddressInput = z.strictObject({
  line1: Text(200),
  line2: Text(200).nullish(),
  city: Text(100),
  postalCode: Text(20).nullish(),
  region: Text(100).nullish(),
});
export type CompanyAddressInput = z.infer<typeof CompanyAddressInput>;

/** What ANAF or VIES said of a number, before any company is made. `unavailable`: the service is down, so the customer types the data. */
export const COMPANY_LOOKUP_STATUSES = ["found", "not_found", "unavailable", "unsupported"] as const;
export const CompanyLookupQuery = z.object({ country: Country, taxId: z.string().trim().min(2).max(24) });
export type CompanyLookupQuery = z.infer<typeof CompanyLookupQuery>;
export const CompanyLookupDto = z.object({
  status: z.enum(COMPANY_LOOKUP_STATUSES),
  source: CompanyVerifier.nullable(),
  country: Country,
  taxId: z.string(),
  /** Null when the service confirms only the number (VIES for Germany, Spain and others): the customer types them. */
  name: z.string().nullable(),
  address: CompanyAddressDto.nullable(),
  vatRegistered: z.boolean(),
});
export type CompanyLookupDto = z.infer<typeof CompanyLookupDto>;

export const NewCompanyRequest = z.strictObject({
  country: Country,
  taxId: z.string().trim().min(2).max(24),
  /** Needed only where the service gave none: a number VIES confirms alone, a service that is down, a country outside the EU. */
  name: Text(200).optional(),
  address: CompanyAddressInput.optional(),
});
export type NewCompanyRequest = z.infer<typeof NewCompanyRequest>;

/** Only what the service did not confirm can be changed by hand. */
export const CompanyPatch = z.strictObject({ name: Text(200).optional(), address: CompanyAddressInput.optional() });
export type CompanyPatch = z.infer<typeof CompanyPatch>;

export const CompanyWorkspaceDto = z.object({
  tenantId: Uuid,
  name: z.string(),
  plan: Plan,
  /** A lower plan waiting for the end of the paid period (decision 9). */
  pendingPlan: Plan.nullable(),
  locations: z.number().int().min(1),
  /** Leaves the subscription at the end of the paid period. */
  cancelAtPeriodEnd: z.boolean(),
  /** Its item at the list price, before the company's volume discount. */
  listMonthlyMinor: z.number().int(),
});
export type CompanyWorkspaceDto = z.infer<typeof CompanyWorkspaceDto>;

export const CompanyDto = z.object({
  id: Uuid,
  name: z.string(),
  country: Country,
  taxId: z.string(),
  address: CompanyAddressDto.nullable(),
  /** Who confirmed the number; null while it is only what the customer typed. */
  verifiedBy: CompanyVerifier.nullable(),
  vatRegistered: z.boolean(),
  /** The service was down at the check: a later check tries again. */
  checkPending: z.boolean(),
  status: Status,
  /** A live subscription, so a new workspace joins it without the Stripe page. */
  hasCard: z.boolean(),
  /** Whether its next Checkout may start with the card trial; when not, what already had one. Card is only known after Checkout. */
  trialAvailable: z.boolean(),
  trialUsedBy: TrialRefusal.nullable(),
  /**
   * The end of the trial a workspace added now would be in: the company's own while its subscription is in the trial, or the account's
   * running trial its Checkout would join (s10-trial-b). Null when it would start none.
   */
  trialEndsAt: IsoDateTime.nullable(),
  /** The account's trial ends too soon for Stripe to take its end (48 hours): a workspace added now pays from today. */
  trialJoinTooLate: z.boolean(),
  currentPeriodEnd: IsoDateTime.nullable(),
  paymentFailedAt: IsoDateTime.nullable(),
  graceUntil: IsoDateTime.nullable(),
  /** All the locations of its workspaces, which the volume discount counts (decision 34). */
  countedLocations: z.number().int(),
  discountPercent: z.number().int(),
  /** A month of all its workspaces, after the discount. */
  monthlyTotalMinor: z.number().int(),
  currency: z.string(),
  workspaces: z.array(CompanyWorkspaceDto),
});
export type CompanyDto = z.infer<typeof CompanyDto>;
export const CompanyList = z.object({ items: z.array(CompanyDto), maxWorkspaces: z.number().int() });
export type CompanyList = z.infer<typeof CompanyList>;

/** The company of a workspace, as its billing screen shows it. Only its owner gets the name and the number. */
export const BillingCompanySummary = z.object({
  id: Uuid,
  mine: z.boolean(),
  name: z.string().nullable(),
  taxId: z.string().nullable(),
  countedLocations: z.number().int(),
  discountPercent: z.number().int(),
  workspaces: z.number().int(),
});
export type BillingCompanySummary = z.infer<typeof BillingCompanySummary>;

/** The payment step: a company of the account, a plan and the locations. */
export const CheckoutRequest = z.strictObject({
  companyId: Uuid,
  plan: Plan,
  locations: z.number().int().min(1).max(500),
  /** Start with the card trial. Refused with `trial_used` when the company may not have one; ignored when it already pays. */
  trial: z.boolean().optional(),
});
export type CheckoutRequest = z.infer<typeof CheckoutRequest>;
/** `url`: the Stripe page, for a company with no card yet. `joined`: the workspace became a new item of the company's subscription. */
export const CheckoutResult = z.object({ url: z.url().nullable(), joined: z.boolean() });
export type CheckoutResult = z.infer<typeof CheckoutResult>;

export const PlanChangeRequest = z.strictObject({ plan: Plan, locations: z.number().int().min(1).max(500) });
export type PlanChangeRequest = z.infer<typeof PlanChangeRequest>;
export const PlanChangeResult = z.object({
  plan: Plan,
  pendingPlan: Plan.nullable(),
  /** When the pending plan takes over: the end of the paid period. */
  pendingFrom: IsoDateTime.nullable(),
  locations: z.number().int(),
  discountPercent: z.number().int(),
});
export type PlanChangeResult = z.infer<typeof PlanChangeResult>;

export const MoveWorkspaceRequest = z.strictObject({ companyId: Uuid });
export type MoveWorkspaceRequest = z.infer<typeof MoveWorkspaceRequest>;

export const CancelResult = z.object({ cancelAtPeriodEnd: z.boolean(), endsAt: IsoDateTime.nullable() });
export type CancelResult = z.infer<typeof CancelResult>;
